import * as React from 'react'
import { Block } from '@basehub/data/replicache/index'
import * as Tooltip from '@radix-ui/react-tooltip'
import { clsx } from 'clsx'
import { PreviewBlockSwitch } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/preview-block-switch'
import {
  BlockLabel,
  BlockWrapperStoreContext,
  createBlockWrapperStore,
} from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/block-wrapper'
import { getBlocksToRender } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/helpers'

import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'

interface BlockPreviewProps {
  block: Block | null | undefined
  children: React.ReactNode
  className?: string
}

export function BlockPreview({
  children,
  block,
  className,
}: BlockPreviewProps) {
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const getChildrenKeys = useWorkingTreeStore((s) => s.tree?.getChildrenKeys)
  const getPath = useWorkingTreeStore((s) => s.tree?.getPath)
  const containerRef = React.useRef<HTMLDivElement>(null)

  const blockWrapperStore = React.useMemo(() => {
    return createBlockWrapperStore({
      scopeRef: containerRef,
      blockId: block?.id,
      isTouched: false,
    })
  }, [block?.id])

  const isOverflowing = React.useMemo(() => {
    // eslint-disable-next-line react-compiler/react-compiler
    if (!containerRef.current) return false
    // eslint-disable-next-line react-compiler/react-compiler
    return containerRef.current.scrollHeight > 140
  }, [containerRef])

  if (!block) return <>{children}</>
  if (
    (block.type === 'component' ||
      block.type === 'instance' ||
      block.type === 'document') &&
    getNode &&
    getChildrenKeys
  ) {
    const blocksToRender = getBlocksToRender(block, {
      getNode,
      getChildrenKeys,
    }).reduce<Array<[Block, string]>>((acc, child) => {
      if (!child || acc.length >= 3) {
        return acc
      }
      const path = getPath?.(child.id, { format: 'string' })
      if (!path) return acc
      return [...acc, [child, path]]
    }, [])

    return (
      <Tooltip.Root>
        <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="bottom"
            align="end"
            sideOffset={6}
            alignOffset={8}
            className={clsx(
              'max-w-[440px] w-full h-full max-h-[220px] fade-in-5 animate-fade-in',
              className
            )}
            style={{
              width: 'clamp(280px,40vw,580px)',
            }}
            asChild
          >
            <div className="bg-dark-base shadow-small-card rounded-lg border flex flex-col border-dark-border-solid p-4 pb-0 overflow-hidden z-tooltip max-h-[220px] relative">
              <h3 className="text-lg font-medium">
                {block.title || `Untitled`}
              </h3>
              <BlockWrapperStoreContext.Provider value={blockWrapperStore}>
                <div
                  className={clsx(
                    'mt-4 flex flex-col gap-2 pb-4 relative rounded-md',
                    isOverflowing && 'pb-0'
                  )}
                  ref={containerRef}
                >
                  {isOverflowing && (
                    <div className="absolute bottom-0 left-0 w-full h-6 z-10 bg-gradient-to-t from-dark-shade to-transparent" />
                  )}
                  {/* Filter  blocks and limit blocks to avoid unusefull rendering*/}
                  {block &&
                    blocksToRender.map(([child, path], i) => (
                      <div className="flex flex-col" key={child.id}>
                        {child.type !== 'comment' && (
                          <BlockLabel
                            parentBlock={block}
                            block={child}
                            index={i}
                            path={path}
                            showActions={false}
                            className="!pb-1"
                          />
                        )}
                        <PreviewBlockSwitch
                          block={child}
                          path={path}
                          parent={block}
                        />
                      </div>
                    ))}
                  {blocksToRender.length === 0 && (
                    <p className="text-xs text-dark-muted">
                      This block is empty. Add some content to see a preview.
                    </p>
                  )}
                </div>
              </BlockWrapperStoreContext.Provider>
            </div>
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    )
  }
  return <>{children}</>
}
